import React from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import TourCard from "../components/TourCard";
import TestimonialsSection from "../components/TestimonialsSection";
import { tours } from "../data/tours";

export default function HomePage() {
  const featuredTours = tours.slice(0, 3);

  return (
    <>
      <SEO
        title="D, Trio Trek — Skardu, Baltistan & Hunza Expeditions"
        description="Private 5 to 7-day expeditions across Skardu, Baltistan and Hunza, run by a local team of native Gilgit-Baltistan tour professionals."
        url="https://dtriotrek.com"
      />

      {/* ── Hero ── */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          {/* D, Trio Trek's own footage. Muted and playsInline because no
              browser autoplays anything else; poster so the hero is never
              blank while 2.8 MB arrives, and on the connections a lot of this
              audience is browsing from, the poster may be all they ever see. */}
          <video
            className="w-full h-full object-cover"
            src="/hero.mp4"
            poster="/images/tours/hero-karakoram.jpg"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            aria-hidden="true"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-black/20" />
        </div>

        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <span className="inline-block text-[11px] font-semibold uppercase tracking-[0.22em] text-white/80 mb-6 animate-fade-in">
            Skardu · Baltistan · Hunza
          </span>
          <h1 className="font-display text-5xl md:text-7xl font-normal tracking-tight text-white mb-6 leading-[1.05] animate-slide-up">
            Private Expeditions
            <br />
            <span className="text-primary-300">Across Baltistan</span>
          </h1>
          <p className="text-white/90 text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
            Cold deserts, 400-year-old forts, the world's second-highest plateau
            and the pine valleys of Basho — five to seven days in our own 4x4s,
            guided by a team from Skardu. Give it a try…
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/tours" className="btn-primary text-lg px-8 py-4">
              See the tours
            </Link>
            <Link
              to="/about"
              className="bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white font-semibold px-8 py-4 rounded-lg transition-all duration-200 inline-block text-lg"
            >
              Who we are
            </Link>
          </div>

        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/60 animate-bounce">
          <span className="text-xs uppercase tracking-widest">Scroll</span>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </section>

      {/* ── Featured Tours ── */}
      <section className="section-y bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="inline-block bg-primary-50 text-primary-700 text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
              Hand-Picked Adventures
            </span>
            <h2 className="section-title">Featured Tours</h2>
            <p className="text-gray-500 max-w-xl mx-auto">
              From soaring mountain peaks to serene island escapes — discover
              our most beloved journeys.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredTours.map((tour) => (
              <TourCard key={tour.id} tour={tour} />
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/tours" className="btn-outline">
              View All Tours
            </Link>
          </div>
        </div>
      </section>

      {/* ── Why Choose Us ── */}
      <section className="section-y bg-primary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="inline-block bg-primary-50 text-primary-700 text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
              Give it a try…
            </span>
            <h2 className="section-title">Why travel with us</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: "🗺️",
                title: "Guides From Here",
                desc: "Balti and Gilgiti guides, working the valleys they grew up in.",
              },
              {
                icon: "🛡️",
                title: "Altitude Handled",
                desc: "Deosai sits above 4,000 m. We pace the ascent and watch for it.",
              },
              {
                icon: "🏡",
                title: "Money Stays Local",
                desc: "Local drivers, local guesthouses, local guides — paid here.",
              },
              {
                icon: "💎",
                title: "Private Departures",
                desc: "Your own 4x4 and your own group, on your own dates.",
              },
            ].map((feature) => (
              <div
                key={feature.title}
                className="bg-white p-8 text-center border border-primary-200"
              >
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="font-bold text-lg text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <TestimonialsSection limit={3} />

      {/* ── CTA ── */}
      <section className="section-y bg-primary-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/2 translate-y-1/2" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">
            Planning a trip to the north?
          </h2>
          <p className="text-primary-100 text-lg mb-8 max-w-xl mx-auto">
            Tell us your dates and what you want to see. Every itinerary is
            built around your flights, and we answer on WhatsApp.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/tours"
              className="bg-white text-primary-600 hover:bg-primary-50 font-semibold px-8 py-4 rounded-lg transition-all duration-200 inline-block"
            >
              Browse Tours
            </Link>
            <Link
              to="/contact"
              className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-primary-600 font-semibold px-8 py-4 rounded-lg transition-all duration-200 inline-block"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
